import React, { createContext, useContext, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import { getGridCellKey, isGridCellSelected } from "../selection/useGridSelection";
import { joinClassNames } from "../utils/classNames";

const EMPTY_SNAPSHOT = Object.freeze({
    selection: null,
    focus: null,
    editingCell: null,
});
const EMPTY_SELECTION_CELL_KEYS = new Set();
const GRID_CELL_SELECTION_EDGE_CLASS_NAMES = Object.freeze({
    top: "grid-core-cell-selected-top",
    bottom: "grid-core-cell-selected-bottom",
    left: "grid-core-cell-selected-left",
    right: "grid-core-cell-selected-right",
});

const GridStateContext = createContext(null);

function isSameCell(cell, rowIdx, colIdx) {
    return cell?.rowIdx === rowIdx && cell?.colIdx === colIdx;
}

function hasStableCellIdentity(cellIdentity) {
    return cellIdentity?.rowId !== undefined && cellIdentity?.rowId !== null && cellIdentity?.columnKey;
}

function isSameStableCell(cell, cellIdentity) {
    if (!hasStableCellIdentity(cellIdentity)) return false;
    return String(cell?.rowId) === String(cellIdentity.rowId) && cell?.columnKey === cellIdentity.columnKey;
}

function isCellFocused(snapshot, cellIdentity) {
    const focus = snapshot.focus ?? snapshot.selection?.focus ?? null;
    if (!focus) return false;

    if (isSameStableCell(focus, cellIdentity)) return true;

    if (Number.isInteger(cellIdentity?.rowIndex) && Number.isInteger(cellIdentity?.columnIndex)) {
        return isSameCell(focus, cellIdentity.rowIndex, cellIdentity.columnIndex);
    }

    return false;
}

function isCellSelected(snapshot, cellIdentity) {
    if (!Number.isInteger(cellIdentity?.rowIndex) || !Number.isInteger(cellIdentity?.columnIndex)) return false;
    return isGridCellSelected(snapshot.selection, cellIdentity.rowIndex, cellIdentity.columnIndex);
}

function addSelectionCellKey(cellKeys, cell) {
    if (!Number.isInteger(cell?.rowIdx) || !Number.isInteger(cell?.colIdx)) return;
    cellKeys.add(getGridCellKey(cell.rowIdx, cell.colIdx));
}

function buildSelectionCellKeys(selection) {
    if (!selection) return EMPTY_SELECTION_CELL_KEYS;
    if (selection.cellKeys instanceof Set) return selection.cellKeys;

    const cellKeys = new Set();
    if (selection.cells instanceof Set) {
        selection.cells.forEach((cell) => addSelectionCellKey(cellKeys, cell));
    } else if (Array.isArray(selection.cells)) {
        selection.cells.forEach((cell) => addSelectionCellKey(cellKeys, cell));
    }

    return cellKeys.size > 0 ? cellKeys : EMPTY_SELECTION_CELL_KEYS;
}

function getSelectionClassName(cellKeys, rowIdx, colIdx, selectionClassName) {
    if (!selectionClassName || !Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return undefined;
    if (!cellKeys.has(getGridCellKey(rowIdx, colIdx))) return undefined;

    return joinClassNames(
        selectionClassName,
        !cellKeys.has(getGridCellKey(rowIdx - 1, colIdx)) && GRID_CELL_SELECTION_EDGE_CLASS_NAMES.top,
        !cellKeys.has(getGridCellKey(rowIdx + 1, colIdx)) && GRID_CELL_SELECTION_EDGE_CLASS_NAMES.bottom,
        !cellKeys.has(getGridCellKey(rowIdx, colIdx - 1)) && GRID_CELL_SELECTION_EDGE_CLASS_NAMES.left,
        !cellKeys.has(getGridCellKey(rowIdx, colIdx + 1)) && GRID_CELL_SELECTION_EDGE_CLASS_NAMES.right,
    );
}

function isCellEditing(snapshot, rowIdx, colIdx) {
    return isSameCell(snapshot.editingCell, rowIdx, colIdx);
}

function areSnapshotsEqual(left, right) {
    return left.selection === right.selection && left.focus === right.focus && left.editingCell === right.editingCell;
}

function createGridStateStore(initialSnapshot = EMPTY_SNAPSHOT) {
    let snapshot = initialSnapshot;
    let selectionCellKeys = buildSelectionCellKeys(initialSnapshot.selection);
    let notifyPending = false;
    const listeners = new Set();

    return {
        prepareSnapshot(nextSnapshot) {
            if (areSnapshotsEqual(snapshot, nextSnapshot)) return;
            if (snapshot.selection !== nextSnapshot.selection) {
                selectionCellKeys = buildSelectionCellKeys(nextSnapshot.selection);
            }
            snapshot = nextSnapshot;
            notifyPending = true;
        },
        flush() {
            if (!notifyPending) return;
            notifyPending = false;
            listeners.forEach((listener) => listener());
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getSnapshot() {
            return snapshot;
        },
        isCellFocused(cellIdentity) {
            return isCellFocused(snapshot, cellIdentity);
        },
        isCellSelected(cellIdentity) {
            return isCellSelected(snapshot, cellIdentity);
        },
        getCellSelectionClassName(cellIdentity, selectionClassName) {
            return getSelectionClassName(selectionCellKeys, cellIdentity?.rowIndex, cellIdentity?.columnIndex, selectionClassName);
        },
        isCellEditing(rowIdx, colIdx) {
            return isCellEditing(snapshot, rowIdx, colIdx);
        },
        getCellClassName(rowIdx, colIdx) {
            return joinClassNames(
                isCellFocused(snapshot, { rowIndex: rowIdx, columnIndex: colIdx }) && "grid-core-cell-focus",
            );
        },
    };
}

export function useGridStateStore(snapshot) {
    const storeRef = useRef(null);
    if (!storeRef.current) storeRef.current = createGridStateStore(snapshot);

    storeRef.current.prepareSnapshot(snapshot);

    useLayoutEffect(() => {
        storeRef.current.flush();
    }, [snapshot]);

    return storeRef.current;
}

export function GridStateProvider({ store, children }) {
    return <GridStateContext.Provider value={store}>{children}</GridStateContext.Provider>;
}

export function useGridCellFocus(cellIdentity) {
    const store = useContext(GridStateContext);
    return useSyncExternalStore(
        store?.subscribe ?? (() => () => {}),
        () => store?.isCellFocused(cellIdentity) ?? false,
        () => false,
    );
}

export function useGridCellFocusClassName(cellIdentity, focusClassName) {
    const focused = useGridCellFocus(cellIdentity);
    return focused ? focusClassName : undefined;
}

export function useGridCellSelection(cellIdentity) {
    const store = useContext(GridStateContext);
    return useSyncExternalStore(
        store?.subscribe ?? (() => () => {}),
        () => store?.isCellSelected(cellIdentity) ?? false,
        () => false,
    );
}

export function useGridCellSelectionClassName(cellIdentity, selectionClassName) {
    const store = useContext(GridStateContext);
    return useSyncExternalStore(
        store?.subscribe ?? (() => () => {}),
        () => store?.getCellSelectionClassName(cellIdentity, selectionClassName) ?? undefined,
        () => undefined,
    );
}

export function buildGridCellIdentity(rowIndex, columnIndex) {
    return {
        rowIndex,
        columnIndex,
        cellKey: Number.isInteger(rowIndex) && Number.isInteger(columnIndex) ? getGridCellKey(rowIndex, columnIndex) : null,
    };
}

export default GridStateContext;
